<template>
    <div :class="`spinner spinner-align-${align}`">
        <label for="spinner-input">{{ fieldLabel }}</label>
        <div :class="['input-wrapper', { 'error': showError && isRequired && isEmpty }]">
            <div class="spinner-wrapper">
                <button @click="decrement" class="spinner-button decrement" type="button">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         stroke-width="2">
                        <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                </button>
                <input
                    type="number"
                    id="spinner-input"
                    v-model="spinnerValue"
                    min="0"
                    @input="emitValues"
                    @focus="onFocus"
                    @blur="onBlur"
                    placeholder="0"
                />
                <button @click="increment" class="spinner-button increment" type="button">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         stroke-width="2">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "FormSpinner",
    props: {
        fieldLabel: {
            type: String,
            required: true,
        },
        align: {
            type: String,
            required: true,
        },
        isRequired: {
            type: Boolean,
            default: false,
        },
        showError: {
            type: Boolean,
            default: false,
        },
        value: {
            type: [Object, String],
            default: () => ({ spinnerValue: null }),
        },
        formId: {
            type: String,
            required: true,
        },
        order: {
            type: Number,
            required: true,
        },
    },
    data() {
        const safeValue = (typeof this.value === "object" && this.value !== null) ? this.value : {};
        return {
            spinnerValue: safeValue.spinnerValue !== undefined ? safeValue.spinnerValue : null, // null başlangıç değeri
            isFocused: false,
        };
    },
    computed: {
        isEmpty() {
            // Spinner için boş kontrolü: null, undefined veya empty string ise boş
            return this.spinnerValue === null || this.spinnerValue === undefined || this.spinnerValue === '';
        }
    },
    watch: {
        value: {
            handler(newValue) {
                const safeValue = (typeof newValue === "object" && newValue !== null) ? newValue : {};
                this.spinnerValue = safeValue.spinnerValue !== undefined ? safeValue.spinnerValue : null;
            },
            deep: true,
        },
        showError: {
            handler() {
                // showError false olduğunda değeri sıfırlama
                // Kullanıcının girdiği değer korunsun
            },
        },
    },
    methods: {
        increment() {
            if (this.spinnerValue === null || this.spinnerValue === undefined) {
                this.spinnerValue = 1; // İlk increment'te 1 olsun
            } else {
                this.spinnerValue++;
            }
            this.emitValues();
        },
        decrement() {
            if (this.spinnerValue === null || this.spinnerValue === undefined) {
                this.spinnerValue = 0; // İlk decrement'te 0 olsun
            } else if (this.spinnerValue > 0) {
                this.spinnerValue--;
            }
            this.emitValues();
        },
        onFocus() {
            this.isFocused = true;
        },
        onBlur() {
            this.isFocused = false;
        },
        emitValues() {
            const spinnerData = {
                fieldLabel: this.fieldLabel,
                spinnerValue: this.spinnerValue,
            };
            this.$emit("input", spinnerData);
            this.$emit("validate", {
                fieldId: this._uid,
                hasError: this.isRequired && this.isEmpty,
            });
            
            // LocalStorage'a kaydet
            localStorage.setItem(`${this.formId}-${this.order}`, JSON.stringify(spinnerData));
            
            console.log('🔄 Spinner field emitted:', {
                fieldId: this.formId,
                rawValue: this.spinnerValue,
                isEmpty: this.isEmpty,
                isRequired: this.isRequired,
                hasError: this.isRequired && this.isEmpty
            });
        },
        loadValues() {
            const savedData = localStorage.getItem(`${this.formId}-${this.order}`);
            if (savedData) {
                try {
                    const parsedData = JSON.parse(savedData);
                    this.spinnerValue = parsedData.spinnerValue !== undefined ? parsedData.spinnerValue : null;
                    
                    console.log('🔄 Spinner values loaded from localStorage:', {
                        fieldId: this.formId,
                        savedValue: parsedData.spinnerValue,
                        currentValue: this.spinnerValue
                    });
                } catch (error) {
                    console.error('❌ Error parsing saved spinner data:', error);
                }
            }
        },
    },
    mounted() {
        // Slug değeri varsa onu kullan, yoksa localStorage'dan yükle
        const hasSlugValue = this.value && typeof this.value === "object" && this.value.spinnerValue !== undefined;
        
        if (!hasSlugValue) {
            this.loadValues();
        }
        
        // İlk emit'i yap
        this.emitValues();
        
        console.log('🔄 Spinner field mounted:', {
            fieldId: this.formId,
            hasSlugValue: hasSlugValue,
            initialValue: this.spinnerValue,
            isEmpty: this.isEmpty
        });
    },
};
</script>

<style lang="scss" scoped>
.spinner {
    width: 100%;
    margin: 0 auto;
    font-family: var(--font-family);
}

// Align sistem - left
.spinner-align-left {
    display: flex;
    align-items: flex-start;
    gap: clamp(0.75rem, 2vw, 1rem);

    > label {
        width: clamp(140px, 20vw, 200px);
        font-size: clamp(0.9rem, 2vw, 1rem);
        font-weight: 500;
        color: var(--text-primary);
        margin: 0;
        line-height: 1.5;
        flex-shrink: 0;
        padding-top: clamp(0.625rem, 1.5vw, 0.75rem);
    }

    .input-wrapper {
        flex: 1;
        min-width: 0;
    }
}

// Align sistem - right
.spinner-align-right {
    display: flex;
    align-items: flex-start;
    gap: clamp(0.75rem, 2vw, 1rem);

    > label {
        width: clamp(140px, 20vw, 200px);
        font-size: clamp(0.9rem, 2vw, 1rem);
        font-weight: 500;
        color: var(--text-primary);
        text-align: right;
        margin: 0;
        line-height: 1.5;
        flex-shrink: 0;
        padding-top: clamp(0.625rem, 1.5vw, 0.75rem);
    }

    .input-wrapper {
        flex: 1;
        min-width: 0;
    }
}

// Top align (default)
.spinner:not(.spinner-align-left):not(.spinner-align-right) {
    > label {
        display: block;
        font-size: clamp(0.9rem, 2vw, 1rem);
        font-weight: 500;
        color: var(--text-primary);
        margin: 0 0 clamp(0.5rem, 1.2vw, 0.75rem) 0;
        line-height: 1.5;
    }
}

// Input wrapper
.input-wrapper {
    position: relative;

    &.error .spinner-wrapper {
        border-color: var(--error);
        background: rgba(239, 68, 68, 0.05);
    }
}

// Spinner wrapper
.spinner-wrapper {
    display: flex;
    align-items: center;
    background: var(--surface-color);
    backdrop-filter: blur(8px);
    border: 2px solid var(--border-color);
    border-radius: clamp(0.5rem, 1.5vw, 0.75rem);
    overflow: hidden;
    transition: all 0.3s ease;
    position: relative;

    &:hover {
        border-color: var(--primary);
        box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
    }

    &:focus-within {
        border-color: var(--primary);
        box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
    }
}

// Input field
input[type="number"] {
    flex: 1;
    padding: clamp(0.75rem, 2vw, 1rem) clamp(0.5rem, 1.5vw, 0.75rem);
    font-size: clamp(0.875rem, 2vw, 1rem);
    font-weight: 500;
    color: var(--text-primary);
    background: transparent;
    border: none;
    text-align: center;
    outline: none;
    font-family: var(--font-family);
    min-width: clamp(3.5rem, 8vw, 4rem);

    &::placeholder {
        color: var(--text-secondary);
        font-weight: 400;
    }

    // Chrome, Safari, Edge, Opera number input spinner gizleme
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    // Firefox number input spinner gizleme
    &[type=number] {
        -moz-appearance: textfield;
    }
}

// Spinner buttons
.spinner-button {
    width: clamp(2.75rem, 6vw, 3rem);
    height: clamp(2.75rem, 6vw, 3rem);
    background: var(--surface-color);
    border: none;
    color: var(--text-secondary);
    cursor: pointer;
    outline: none;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    position: relative;
    flex-shrink: 0;

    svg {
        width: clamp(1rem, 2.2vw, 1.125rem);
        height: clamp(1rem, 2.2vw, 1.125rem);
        transition: all 0.3s ease;
    }

    &:hover {
        background: var(--surface-hover);
        color: var(--primary);
        transform: scale(1.05);

        svg {
            transform: scale(1.1);
        }
    }

    &:active {
        transform: scale(0.95);
        background: var(--primary);
        color: white;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none;

        &:hover {
            background: var(--surface-color);
            color: var(--text-secondary);
            transform: none;
        }
    }
}

// Decrement button
.decrement {
    border-right: 1px solid var(--border-color);

    &:hover {
        border-right-color: transparent;
    }
}

// Increment button  
.increment {
    border-left: 1px solid var(--border-color);

    &:hover {
        border-left-color: transparent;
    }
}

// Mobil optimizasyonları
@media (max-width: 768px) {
    .spinner-align-left,
    .spinner-align-right {
        flex-direction: column;
        gap: 0;

        > label {
            width: 100%;
            text-align: left !important;
            padding-top: 0;
            margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
        }

        .input-wrapper {
            width: 100%;
        }
    }

    .spinner-wrapper {
        border-radius: clamp(0.375rem, 1vw, 0.5rem);
    }

    input[type="number"] {
        padding: clamp(0.625rem, 1.5vw, 0.75rem) clamp(0.375rem, 1vw, 0.5rem);
        font-size: clamp(0.8rem, 1.8vw, 0.875rem);
        min-width: clamp(3rem, 6vw, 3.5rem);
    }

    .spinner-button {
        width: clamp(2.5rem, 5vw, 2.75rem);
        height: clamp(2.5rem, 5vw, 2.75rem);

        svg {
            width: clamp(0.875rem, 2vw, 1rem);
            height: clamp(0.875rem, 2vw, 1rem);
        }
    }
}

// Küçük ekranlar için
@media (max-width: 480px) {
    .spinner-align-left,
    .spinner-align-right {
        > label {
            font-size: 0.875rem;
            margin-bottom: 0.5rem;
        }
    }

    .spinner:not(.spinner-align-left):not(.spinner-align-right) {
        > label {
            font-size: 0.875rem;
            margin-bottom: 0.625rem;
        }
    }

    .spinner-wrapper {
        border-radius: 0.375rem;
    }

    input[type="number"] {
        padding: 0.625rem 0.375rem;
        font-size: 0.8rem;
        min-width: 3rem;
    }

    .spinner-button {
        width: 2.5rem;
        height: 2.5rem;

        &:hover {
            transform: scale(1.02);
        }

        &:active {
            transform: scale(0.98);
        }

        svg {
            width: 0.875rem;
            height: 0.875rem;
        }
    }
}
</style>